import { HTTP_STATUS_CODE } from './constants/httpStatusCode'

interface ApiErrorOptions {
  title?: string
  message: string
  status?: number
  statusText?: string
}

interface ApiErrorResponse {
  message?: string
  error?: string
  errors?: string[]
  detail?: object[]
}

const getDefaultErrorMessage = (status: number) => {
  const errorMap: Record<number, { title: string; message: string }> = {
    [HTTP_STATUS_CODE.BAD_REQUEST]: {
      title: 'Bad Request',
      message: 'The request was invalid'
    },
    [HTTP_STATUS_CODE.UNAUTHORIZED]: {
      title: 'Unauthorized',
      message: 'You are not authorized to access this resource'
    },
    [HTTP_STATUS_CODE.FORBIDDEN]: {
      title: 'Forbidden',
      message: 'You do not have permission to access this resource'
    },
    [HTTP_STATUS_CODE.NOT_FOUND]: {
      title: 'Not Found',
      message: 'The requested resource was not found'
    },
    [HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR]: {
      title: 'Internal Server Error',
      message: 'An internal server error occurred'
    },
    [HTTP_STATUS_CODE.BAD_GATEWAY]: {
      title: 'Bad Gateway',
      message: 'The server received an invalid response'
    },
    [HTTP_STATUS_CODE.SERVICE_UNAVAILABLE]: {
      title: 'Service Unavailable',
      message: 'The service is temporarily unavailable'
    },
    [HTTP_STATUS_CODE.GATEWAY_TIMEOUT]: {
      title: 'Gateway Timeout',
      message: 'The server request timed out'
    }
  }

  return errorMap[status] || { title: 'Unknown Error', message: 'Please check the logs' }
}

export class ApiError extends Error {
  public readonly message: string
  public readonly title?: string
  public readonly status?: number
  public readonly statusText?: string

  constructor({ message, title, status, statusText }: ApiErrorOptions) {
    super(message)
    this.message = message
    this.title = title
    this.status = status
    this.statusText = statusText
    Object.setPrototypeOf(this, ApiError.prototype)
  }

  static parseFromResponse = async (response: Response): Promise<ApiError> => {
    try {
      const respBody = await response.text()
      let respData: ApiErrorResponse

      try {
        respData = JSON.parse(respBody)
      } catch {
        return new ApiError({
          title: 'Error',
          message: respBody,
          status: response.status,
          statusText: response.statusText
        })
      }

      const message = this.formatErrorMessage(respData)

      return new ApiError({
        title: respData.message || getDefaultErrorMessage(response.status).title,
        message,
        status: response.status,
        statusText: response.statusText
      })
    } catch {
      const { title, message } = getDefaultErrorMessage(response.status)
      return new ApiError({
        title,
        message,
        status: response.status,
        statusText: response.statusText
      })
    }
  }

  private static formatErrorMessage(respData: ApiErrorResponse) {
    if (Array.isArray(respData.errors)) {
      return respData.errors.join('\n')
    }

    if (Array.isArray(respData.detail)) {
      return respData.detail.map((item) => `(${Object.values(item)})`).join(', ')
    }

    if (respData.error) {
      return respData.error
    }

    return getDefaultErrorMessage(HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR).message
  }
}
